import {
  educationModuleLogResponseSchema,
  educationModuleResponseSchema,
  educationModuleSlideResponseSchema,
  type EducationModuleTarget,
} from '@entities/education-module';
import { api } from '@shared/api/client';
import { createQuery } from '@shared/lib/query-helpers';

export type EducationModuleMode = 'resume' | 'review';

const targetKey = (target: EducationModuleTarget) => ({
  profileRef: target.profile_ref,
  moduleId: target.module_id,
  contextKind: target.context.kind,
  contextId: target.context.id,
});

const targetParams = (target: EducationModuleTarget, locale: string) => ({
  profile_ref: target.profile_ref,
  context_kind: target.context.kind,
  context_id: target.context.id,
  locale,
});

export const educationModuleKeys = {
  all: ['education-module'] as const,
  target: (target: EducationModuleTarget) =>
    [...educationModuleKeys.all, targetKey(target)] as const,
  module: (target: EducationModuleTarget, locale: string) =>
    [...educationModuleKeys.target(target), 'module', { locale }] as const,
  slide: (target: EducationModuleTarget, slideId: string, locale: string) =>
    [...educationModuleKeys.target(target), 'slide', { slideId, locale }] as const,
  log: (target: EducationModuleTarget, emLogId: string) =>
    [...educationModuleKeys.target(target), 'log', { emLogId, projection: 'runtime' }] as const,
};

export const educationModuleQuery = (target: EducationModuleTarget, locale: string) =>
  createQuery({
    queryKey: educationModuleKeys.module(target, locale),
    path: `/api/v1/education-modules/${encodeURIComponent(target.module_id)}`,
    schema: educationModuleResponseSchema,
    requestOptions: { params: targetParams(target, locale), timeout: 60_000 },
    staleTime: 30_000,
  });

export const educationModuleSlideQuery = (
  target: EducationModuleTarget,
  slideId: string,
  locale: string
) =>
  createQuery({
    queryKey: educationModuleKeys.slide(target, slideId, locale),
    path: `/api/v1/education-modules/${encodeURIComponent(target.module_id)}/slides/${encodeURIComponent(slideId)}`,
    schema: educationModuleSlideResponseSchema,
    requestOptions: { params: targetParams(target, locale), timeout: 60_000 },
    staleTime: 30_000,
    enabled: slideId.length > 0,
  });

export const educationModuleLogQuery = (target: EducationModuleTarget, emLogId: string) =>
  createQuery({
    queryKey: educationModuleKeys.log(target, emLogId),
    path: `/api/v1/user-profiles/${encodeURIComponent(target.profile_ref)}/education-module-logs/${encodeURIComponent(emLogId)}`,
    schema: educationModuleLogResponseSchema,
    requestOptions: { params: { projection: 'runtime' } },
    staleTime: 0,
    enabled: emLogId.length > 0,
  });

const commandOptions = (idempotencyKey: string) => ({
  headers: { 'Idempotency-Key': idempotencyKey },
  schema: educationModuleLogResponseSchema,
});

export const createOrResumeEducationModuleLog = async (input: {
  target: EducationModuleTarget;
  locale: string;
  idempotencyKey: string;
}) =>
  await api.post(
    `/api/v1/user-profiles/${encodeURIComponent(input.target.profile_ref)}/education-module-logs`,
    {
      module_id: input.target.module_id,
      context: input.target.context,
      locale: input.locale,
    },
    commandOptions(input.idempotencyKey)
  );

export const putEducationModuleSlideLog = async (input: {
  target: EducationModuleTarget;
  emLogId: string;
  slideId: string;
  state: 'in_progress' | 'completed' | 'skipped';
  expectedRevision: number;
  idempotencyKey: string;
}) =>
  await api.put(
    `/api/v1/user-profiles/${encodeURIComponent(input.target.profile_ref)}/education-module-logs/${encodeURIComponent(input.emLogId)}/slide-logs/${encodeURIComponent(input.slideId)}`,
    { state: input.state, expected_revision: input.expectedRevision },
    commandOptions(input.idempotencyKey)
  );

export const putEducationModuleQuestionLog = async (input: {
  target: EducationModuleTarget;
  emLogId: string;
  slideId: string;
  questionId: string;
  answerIds: string[];
  expectedRevision: number;
  idempotencyKey: string;
}) =>
  await api.put(
    `/api/v1/user-profiles/${encodeURIComponent(input.target.profile_ref)}/education-module-logs/${encodeURIComponent(input.emLogId)}/slide-logs/${encodeURIComponent(input.slideId)}/question-logs/${encodeURIComponent(input.questionId)}`,
    { answer_ids: input.answerIds, expected_revision: input.expectedRevision },
    commandOptions(input.idempotencyKey)
  );

export const completeEducationModuleLog = async (input: {
  target: EducationModuleTarget;
  emLogId: string;
  expectedRevision: number;
  idempotencyKey: string;
}) =>
  await api.post(
    `/api/v1/user-profiles/${encodeURIComponent(input.target.profile_ref)}/education-module-logs/${encodeURIComponent(input.emLogId)}/complete`,
    { expected_revision: input.expectedRevision },
    commandOptions(input.idempotencyKey)
  );
